import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { titleCase } from "@/lib/format";
import { PageHeader, DataTable } from "@/components/shared";
import { DefaultLeadOwner } from "@/components/DefaultLeadOwner";
import { FormDialog } from "@/components/EntityForm";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MapPin, Plus, Pencil, Trash2, Users } from "lucide-react";
import { run } from "@/components/agent/AgentPanels";

const METHODS = [{ value: "round_robin", label: "Turn by turn" }, { value: "least_open", label: "Fewest open leads" }];
const RULE_FIELDS = [
  { name: "name", label: "Rule name", required: true },
  { name: "business", label: "Applies to", type: "select", options: [{ value: "both", label: "Insurance + Loan" }, { value: "insurance", label: "Insurance" }, { value: "loan", label: "Loan" }] },
  { name: "method", label: "How to pick employee", type: "select", options: METHODS },
  { name: "priority", label: "Priority (lower runs first)", type: "number" },
  { name: "cities", label: "Cities (comma-separated)", full: true },
  { name: "pincodes", label: "PIN codes or prefixes (comma-separated, e.g. 110001, 4000)", full: true },
  { name: "active", label: "Active?", type: "select", options: [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }] },
];

function StaffPicker({ rule, staff, onSave }) {
  const [open, setOpen] = useState(false);
  const [ids, setIds] = useState(rule.user_ids || []);
  const toggle = (id) => setIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setIds(rule.user_ids || []); }}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="h-8" data-testid="rule-staff-btn"><Users className="h-4 w-4 mr-1" />{(rule.user_ids || []).length} staff</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Employees for {rule.name}</DialogTitle></DialogHeader>
        <div className="max-h-72 overflow-y-auto space-y-2">
          {staff.map((u) => (
            <Label key={u.id} className="flex items-center gap-2 text-sm font-normal">
              <Checkbox checked={ids.includes(u.id)} onCheckedChange={() => toggle(u.id)} data-testid="rule-staff-checkbox" />{u.name} <span className="text-muted-foreground">({titleCase(u.role)} · {u.employee_id})</span>
            </Label>
          ))}
        </div>
        <DialogFooter><Button onClick={async () => { if (await onSave(ids)) setOpen(false); }} data-testid="rule-staff-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const toBody = (r, v) => ({
  ...r, ...v, active: (v.active ?? (r.active ? "yes" : "no")) === "yes",
  user_ids: v.user_ids || r.user_ids || [],
});

function RulesTab() {
  const { hasPerm } = useAuth();
  const [rules, setRules] = useState([]);
  const [users, setUsers] = useState([]);
  const load = useCallback(() => api.get("/assignment-rules").then(({ data }) => setRules(data)), []);
  useEffect(() => { load(); api.get("/users").then(({ data }) => setUsers(data.filter((u) => u.active !== false))).catch(() => {}); }, [load]);
  const can = hasPerm("assignment_rules:manage");
  const name = (id) => users.find((u) => u.id === id)?.name || "?";
  const save = (r, v) => api.put(`/assignment-rules/${r.id}`, toBody(r, v)).then(load);
  const create = async (v) => {
    await api.post("/assignment-rules", { ...v, active: v.active !== "no" }); load();
  };
  const cols = [
    { key: "name", header: "Rule", render: (r) => <div><div className="font-semibold">{r.name}</div><div className="text-xs text-muted-foreground">Priority {r.priority} · {r.business === "both" ? "Insurance + Loan" : titleCase(r.business)}</div></div> },
    { key: "area", header: "Area", render: (r) => <div className="text-sm">{(r.cities || []).join(", ")}{r.pincodes?.length > 0 && <div className="font-mono text-xs text-muted-foreground">PIN {r.pincodes.join(", ")}</div>}</div> },
    { key: "method", header: "Method", render: (r) => METHODS.find((m) => m.value === r.method)?.label },
    { key: "staff", header: "Employees", render: (r) => can ? <StaffPicker rule={r} staff={users} onSave={(ids) => run(() => save(r, { user_ids: ids }), "Employees updated")} /> : <span className="text-sm">{(r.user_ids || []).map(name).join(", ")}</span> },
    { key: "active", header: "Active", render: (r) => <span className={r.active ? "text-emerald-600 font-semibold text-sm" : "text-muted-foreground text-sm"}>{r.active ? "Yes" : "No"}</span> },
    ...(can ? [{ key: "a", header: "", render: (r) => (
      <div className="flex gap-1 justify-end">
        <FormDialog title={`Edit ${r.name}`} fields={RULE_FIELDS} initial={{ ...r, cities: (r.cities || []).join(", "), pincodes: (r.pincodes || []).join(", "), active: r.active ? "yes" : "no" }}
          onSubmit={(v) => save(r, v)} submitLabel="Update" trigger={<Button size="icon" variant="ghost" className="h-8 w-8" data-testid="rule-edit-btn"><Pencil className="h-4 w-4" /></Button>} />
        <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" data-testid="rule-delete-btn"
          onClick={() => window.confirm(`Delete rule "${r.name}"? Already-assigned leads are not changed.`) && run(() => api.delete(`/assignment-rules/${r.id}`), "Rule deleted").then(load)}><Trash2 className="h-4 w-4" /></Button>
      </div>) }] : []),
  ];
  return (
    <div>
      <div className="flex justify-between items-center mb-3 gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground">New leads without an assignee are routed by the first matching rule (PIN first, then city). If no rule matches, staff-created leads stay with the creator, and website or Meta leads go to the default lead owner.</p>
        {can && <FormDialog title="New Assignment Rule" fields={RULE_FIELDS} initial={{ business: "both", method: "round_robin", priority: 100, active: "yes" }}
          description="After saving, click the staff button on the rule to choose employees." onSubmit={create} submitLabel="Create Rule" testId="create-rule-dialog"
          trigger={<Button data-testid="create-rule-btn"><Plus className="h-4 w-4 mr-1.5" />Add Rule</Button>} />}
      </div>
      <DataTable columns={cols} rows={rules} rowTestId="assignment-rule-row" emptyText="No rules yet. Staff-created leads stay with the creator; website leads go to the default lead owner." />
    </div>
  );
}

function AreaTab() {
  const [group, setGroup] = useState("city");
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/reports/area", { params: { group_by: group } }).then(({ data }) => setRows(data.items)).catch(() => toast.error("Could not load area report")); }, [group]);
  const exportCsv = () => {
    const keys = ["area", "insurance_leads", "insurance_converted", "insurance_lost", "loan_leads", "loan_disbursed", "loan_lost"];
    const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => `"${r[k]}"`).join(","))].join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = `area-report-${group}.csv`; a.click();
  };
  const cols = [
    { key: "area", header: group === "city" ? "City" : "PIN", render: (r) => <span className="font-semibold">{r.area}</span> },
    { key: "insurance_leads", header: "Ins. leads" }, { key: "insurance_converted", header: "Converted" }, { key: "insurance_lost", header: "Lost" },
    { key: "loan_leads", header: "Loan leads" }, { key: "loan_disbursed", header: "Disbursed" }, { key: "loan_lost", header: "Rejected/Closed" },
  ];
  return (
    <div>
      <div className="flex gap-3 mb-3">
        <Select value={group} onValueChange={setGroup}><SelectTrigger className="w-44" data-testid="area-group-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="city">By city</SelectItem><SelectItem value="pincode">By PIN code</SelectItem></SelectContent></Select>
        <Button variant="outline" onClick={exportCsv} data-testid="area-export-btn">Export CSV</Button>
      </div>
      <DataTable columns={cols} rows={rows} rowTestId="area-report-row" emptyText="No leads yet" />
    </div>
  );
}

export default function AssignmentRules() {
  const { user } = useAuth();
  const canDefault = ["super_admin", "admin", "sales_manager"].includes(user?.role);
  return (
    <div>
      <PageHeader title="Area Routing" subtitle="Auto-assign leads by city / PIN and see area-wise performance" icon={MapPin} />
      {canDefault && <DefaultLeadOwner />}
      <Tabs defaultValue="rules">
        <TabsList className="mb-4"><TabsTrigger value="rules" data-testid="tab-rules">Assignment Rules</TabsTrigger><TabsTrigger value="area" data-testid="tab-area">Area Report</TabsTrigger></TabsList>
        <TabsContent value="rules"><RulesTab /></TabsContent>
        <TabsContent value="area"><AreaTab /></TabsContent>
      </Tabs>
    </div>
  );
}
